// src/services/tenantConfigCache.ts
// Centralized configuration cache to prevent over-fetching

import { tenantConfigAPI } from './tenantConfigAPI';
import { logger } from '../utils/logger';

const configLogger = logger.createServiceLogger('ConfigCache');

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  promise?: Promise<T>;
}

interface TenantConfigValues {
  ranks: string[];
  assignments: string[];
  departments: string[];
  shifts: string[];
  inventoryCategories: string[];
  inventoryLocations: string[];
  assignmentReasons: string[];
}

class TenantConfigCache {
  private cache = new Map<string, CacheEntry<any>>();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  private readonly MAX_CACHE_SIZE = 100;

  private getCacheKey(configType: string, tenantId?: string): string {
    return `${tenantId || 'default'}_${configType}`;
  }

  private isExpired(entry: CacheEntry<any>): boolean {
    return Date.now() - entry.timestamp > this.CACHE_TTL;
  }

  private evictExpired(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];
    
    // Collect expired keys
    this.cache.forEach((entry, key) => {
      if (now - entry.timestamp > this.CACHE_TTL) {
        keysToDelete.push(key);
      }
    });
    
    // Delete expired entries
    keysToDelete.forEach(key => {
      this.cache.delete(key);
      configLogger.debug('Evicted expired cache entry', { key });
    });

    // Evict oldest entries if cache is too large
    if (this.cache.size > this.MAX_CACHE_SIZE) {
      const entries = Array.from(this.cache.entries());
      entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
      const toDelete = entries.slice(0, this.cache.size - this.MAX_CACHE_SIZE);
      
      toDelete.forEach(([key]) => {
        this.cache.delete(key);
        configLogger.debug('Evicted old cache entry', { key });
      });
    }
  }

  private async fetchConfig<T>(
    configType: string,
    fetcher: () => Promise<T>,
    tenantId?: string
  ): Promise<T> {
    const key = this.getCacheKey(configType, tenantId);
    const entry = this.cache.get(key);

    // Return cached data if valid
    if (entry && !this.isExpired(entry)) {
      configLogger.debug('Cache hit', { configType, tenantId });
      return entry.data;
    }

    // Return in-flight promise if exists
    if (entry?.promise) {
      configLogger.debug('Request deduplication', { configType, tenantId });
      return entry.promise;
    }

    // Fetch new data
    configLogger.info('Cache miss - fetching configuration', { configType, tenantId });
    
    const promise = fetcher().then(data => {
      // Store in cache
      this.cache.set(key, {
        data,
        timestamp: Date.now()
      });
      
      configLogger.debug('Configuration cached', { 
        configType, 
        tenantId,
        dataLength: Array.isArray(data) ? data.length : 'N/A'
      });
      
      // Clean up expired entries
      this.evictExpired();
      
      return data;
    }).catch(error => {
      // Remove failed promise from cache
      this.cache.delete(key);
      configLogger.error('Configuration fetch failed', {
        configType,
        tenantId,
        error: error.message
      }, error);
      throw error;
    });

    // Store promise to prevent duplicate requests
    this.cache.set(key, {
      data: null,
      timestamp: Date.now(),
      promise
    });

    return promise;
  }

  // Public API methods with caching
  async getUserRanks(tenantId?: string): Promise<string[]> {
    return this.fetchConfig(
      'user.ranks',
      () => tenantConfigAPI.getUserRanks(tenantId),
      tenantId
    );
  }

  async getUserAssignments(tenantId?: string): Promise<string[]> {
    return this.fetchConfig(
      'user.assignments',
      () => tenantConfigAPI.getUserAssignments(tenantId),
      tenantId
    );
  }

  async getUserDepartments(tenantId?: string): Promise<string[]> {
    return this.fetchConfig(
      'user.departments',
      () => tenantConfigAPI.getUserDepartments(tenantId),
      tenantId
    );
  }

  async getUserShifts(tenantId?: string): Promise<string[]> {
    return this.fetchConfig(
      'user.shifts',
      () => tenantConfigAPI.getUserShifts(tenantId),
      tenantId
    );
  }

  async getInventoryCategories(tenantId?: string): Promise<string[]> {
    return this.fetchConfig(
      'inventory.categories',
      () => tenantConfigAPI.getInventoryCategories(tenantId),
      tenantId
    );
  }

  async getInventoryLocations(tenantId?: string): Promise<string[]> {
    return this.fetchConfig(
      'inventory.locations',
      () => tenantConfigAPI.getInventoryLocations(tenantId),
      tenantId
    );
  }

  async getAssignmentReasons(tenantId?: string): Promise<string[]> {
    return this.fetchConfig(
      'assignment.reasons',
      () => tenantConfigAPI.getAssignmentReasons(tenantId),
      tenantId
    );
  }

  // Batch fetch all configurations at once
  async getAllConfigurations(tenantId?: string): Promise<TenantConfigValues> {
    const timer = logger.startTimer('ConfigCache', 'getAllConfigurations');
    
    configLogger.info('Fetching all tenant configurations', { tenantId });

    try {
      // Use Promise.allSettled to handle partial failures gracefully
      const results = await Promise.allSettled([
        this.getUserRanks(tenantId),
        this.getUserAssignments(tenantId),
        this.getUserDepartments(tenantId),
        this.getUserShifts(tenantId),
        this.getInventoryCategories(tenantId),
        this.getInventoryLocations(tenantId),
        this.getAssignmentReasons(tenantId)
      ]);

      const [
        ranksResult,
        assignmentsResult,
        departmentsResult,
        shiftsResult,
        categoriesResult,
        locationsResult,
        reasonsResult
      ] = results;

      // Extract data with fallbacks - handle both rejected promises AND null values
      const configs: TenantConfigValues = {
        ranks: (ranksResult.status === 'fulfilled' && ranksResult.value) ? ranksResult.value : [],
        assignments: (assignmentsResult.status === 'fulfilled' && assignmentsResult.value) ? assignmentsResult.value : [],
        departments: (departmentsResult.status === 'fulfilled' && departmentsResult.value) ? departmentsResult.value : [],
        shifts: (shiftsResult.status === 'fulfilled' && shiftsResult.value) ? shiftsResult.value : [],
        inventoryCategories: (categoriesResult.status === 'fulfilled' && categoriesResult.value) ? categoriesResult.value : [],
        inventoryLocations: (locationsResult.status === 'fulfilled' && locationsResult.value) ? locationsResult.value : [],
        assignmentReasons: (reasonsResult.status === 'fulfilled' && reasonsResult.value) ? reasonsResult.value : []
      };

      const successCount = results.filter(result => result.status === 'fulfilled').length;

      configLogger.info('Batch configuration fetch completed', {
        tenantId,
        successCount,
        totalRequests: 7,
        cacheHits: this.getCacheStats()
      });

      timer(); // Complete timing
      
      return configs;

    } catch (error) {
      timer(); // Complete timing even on error
      configLogger.error('Batch configuration fetch failed', {
        tenantId,
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error instanceof Error ? error : undefined);
      throw error;
    }
  }

  // Cache management
  invalidateConfig(configType: string, tenantId?: string): void {
    const key = this.getCacheKey(configType, tenantId);
    this.cache.delete(key);
    configLogger.info('Configuration cache invalidated', { configType, tenantId });
  }

  invalidateAll(tenantId?: string): void {
    if (tenantId) {
      // Invalidate all configs for specific tenant
      const keysToDelete: string[] = [];
      this.cache.forEach((_, key) => {
        if (key.startsWith(`${tenantId}_`)) {
          keysToDelete.push(key);
        }
      });
      
      keysToDelete.forEach(key => {
        this.cache.delete(key);
      });
      
      configLogger.info('All configurations invalidated for tenant', { tenantId });
    } else {
      // Clear entire cache
      this.cache.clear();
      configLogger.info('Entire configuration cache cleared');
    }
  }

  getCacheStats(): { size: number; hitRate?: number } {
    return {
      size: this.cache.size
    };
  }

  // Preload configurations for better UX
  async preloadConfigurations(tenantId?: string): Promise<void> {
    configLogger.info('Preloading configurations', { tenantId });
    
    try {
      await this.getAllConfigurations(tenantId);
      configLogger.info('Configuration preload completed', { tenantId });
    } catch (error) {
      configLogger.warn('Configuration preload failed', {
        tenantId,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      // Don't throw - preloading is optional
    }
  }
}

// Export singleton instance
export const tenantConfigCache = new TenantConfigCache();
